import { camelCase, compact, get, intersectionBy, isEmpty, sortBy, unionBy } from 'lodash';
import React from 'react';
import { constants, messageFormat, sortByRanking } from '@clubmed/utils';
import type { CMSProps, LineProps } from 'Cms/product-accommodations/resolver';
import 'Cms/product-accommodations/resolver';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { ProductInformationSliderContainer } from 'Components/widgets/product-information-slider/ProductInformationSlider.container';
import { ProductRoomsComparatorLauncher } from 'Components/widgets/product-rooms-comparator/ProductRoomsComparatorLauncher';
import { ProductRoomCard } from 'Components/widgets/product-room-card/ProductRoomCard';
import { ProductRoomDetail } from 'Components/widgets/product-room-detail/ProductRoomDetail';
import { COMFORT_TYPE_SUITE } from 'Utils/proposal/constants';
import type { ComponentProps, ReactNode } from 'react';
import { asAccommodationsCategories, asWidgetProps, castAs } from 'Src/types/cast-helpers';
const { PRODUCT_TYPES, RANKINGS } = constants;
const DEFAULT_RANKING = RANKINGS.LOW;
type ProductAccommodationsIconsList = {
  surfaceItem?: LabeledIcon | null | undefined;
  capacityItem?: LabeledIcon | null | undefined;
  outsideFacilitiesItems?: Array<LabeledIcon> | null | undefined;
  strengths?: Array<AccommodationStrength> | null | undefined;
  viewItem?: LabeledIcon | null | undefined;
};
export type BELineProps = LineProps & {
  accommodationCategory?: {
    rankedCategories?: Array<{
      id?: string;
      ranking?: string;
      price?: {
        totalWithoutDiscount?: FormattedPrice;
        total?: FormattedPrice;
        diffPrice?: string;
      };
    }>;
  };
};
export type AccommodationsCategory = {
  area: {
    max: number;
    min: number;
    unit: string;
  };
  bathroomEquipments: Array<AccommodationEquipment>;
  categoryAccommodations: Array<{
    accommodation: Accommodation;
    accommodationIndex: number;
    categoryLabel: string;
    categoryIndex: number;
    colorId: string;
    resortComfort: number;
    streetView?: Array<StreetViewWithAccommodationCategoryId>;
    i18n: CMSProps;
  }>;
  categoryId: string;
  categoryIndex: number;
  categoryLabel: string;
  equipments: Array<AccommodationEquipment>;
  exclusiveCollection: boolean;
  maxCapacity: number;
  media: AccommodationMedia;
  minCapacity: number;
  price?: {
    initialPrice?: FormattedPrice;
    bestPrice?: FormattedPrice;
    diffPrice?: string;
  };
  services: Array<AccommodationEquipment>;
  strengths: Array<AccommodationEquipment>;
};
export function getSurfaceIconItem(
  i18n: CMSProps,
  surface: string | null | undefined,
  roomType: string,
): LabeledIcon {
  return {
    icon: ICONS[roomType] || null,
    labels: [get(i18n, camelCase(roomType)), surface || (roomType && ' ') || ''],
  };
}
export function getCapacityIconItem(
  i18n: CMSProps,
  capacity: number | null | undefined,
): LabeledIcon | null | undefined {
  const peopleLabel = get(i18n, 'numberOfPeopleLabel');

  if (!capacity || !peopleLabel) {
    return null;
  }

  return {
    icon: ICONS.PEOPLE,
    labels: [
      get(i18n, 'capacityOfPeopleLabel'),
      messageFormat(peopleLabel).format({
        count: capacity,
      }),
    ],
  };
}
export function getViewIconItem(view?: AccommodationView): LabeledIcon | null | undefined {
  return view
    ? {
        icon: ICONS[get(view, 'id')] || ICONS.TRIDENT1,
        labels: [view.label],
      }
    : null;
}
export function getOutsideFacilitiesIconItems(
  outsideFacilities?: Array<AccommodationEquipment>,
): Array<LabeledIcon> | null | undefined {
  if (isEmpty(outsideFacilities)) {
    return null;
  }

  return outsideFacilities.map((item) => {
    return {
      icon: ICONS[get(item, 'id')],
      labels: [item.label],
    };
  });
}

function getServiceIconItem(service): LabeledIcon | null | undefined {
  return service
    ? {
        icon: ICONS[get(service, 'id')] || ICONS.TRIDENT1,
        labels: [service.label],
      }
    : null;
}

export function aggregateIconList({
  surfaceItem,
  capacityItem,
  outsideFacilitiesItems,
  strengths,
  viewItem,
}: ProductAccommodationsIconsList) {
  const maxAccomodationsToDisplay = 4;
  const accomodationsWithoutStrengthsCount = [
    capacityItem,
    ...(outsideFacilitiesItems || []),
    viewItem,
  ].filter((item) => get(item, 'labels.0')).length;
  const availableSpacesForStrengths =
    maxAccomodationsToDisplay - accomodationsWithoutStrengthsCount;
  const strengthsItems = (strengths || [])
    .slice(0, availableSpacesForStrengths)
    .map((strength) => ({
      icon: ICONS[get(strength, 'id')] || ICONS.TRIDENT1,
      labels: [get(strength, 'label') || ''],
    }));
  // -expect-error TODO(flow-migration)
  return compact([
    surfaceItem,
    capacityItem,
    ...(outsideFacilitiesItems || []),
    viewItem,
    ...strengthsItems,
  ] as Array<LabeledIcon | null | undefined>)
    .filter(({ labels = [] }) => labels.length && labels[0])
    .slice(0, 4);
}

function mapAccommodationStrengths(i18n: CMSProps, accommodation: Accommodation) {
  const { maxCapacity, roomType, strengths, surface, view, outsideFacilities } = accommodation;
  const surfaceItem = getSurfaceIconItem(i18n, surface, roomType);
  const capacityItem = getCapacityIconItem(i18n, maxCapacity);
  const viewItem = getViewIconItem(view);
  const outsideFacilitiesItems = getOutsideFacilitiesIconItems(outsideFacilities);
  return {
    strengths: aggregateIconList({
      surfaceItem,
      capacityItem,
    }),
    strengthsCompact: [surfaceItem?.labels, capacityItem?.labels].filter(Boolean),
    strengthsAlternate: aggregateIconList({
      outsideFacilitiesItems,
      viewItem,
      strengths,
    }),
  };
}

export function mapAccommodationServices(
  i18n: CMSProps,
  accommodation: Accommodation,
  isExclusiveCollection: boolean,
): Array<{
  label: string | null;
  items: Array<LabeledIcon | null | undefined> | boolean;
}> {
  const { bathroomEquipments = [], equipments = [], services = [] } = accommodation;
  const isSuiteAccommodation = accommodation?.comfortId === COMFORT_TYPE_SUITE;
  return [
    {
      label: isExclusiveCollection || isSuiteAccommodation ? get(i18n, 'servicesLabel', '') : null,
      items: Array.isArray(services) && services.map((service) => getServiceIconItem(service)),
    },
    {
      label: get(i18n, 'equipmentLabel', ''),
      items: equipments.map((equipment) => getServiceIconItem(equipment)),
    },
    {
      label: get(i18n, 'bathroomLabel', ''),
      items: bathroomEquipments.map((equipment) => getServiceIconItem(equipment)),
    },
    {
      label: !(isExclusiveCollection || isSuiteAccommodation)
        ? get(i18n, 'servicesLabel', '')
        : null,
      items: Array.isArray(services) && services.map((service) => getServiceIconItem(service)),
    },
  ].filter((service) => service.label);
}

function getRankingIndex(ranking) {
  return Object.values(RANKINGS).findIndex((r) => r === ranking);
}

function isProductExclusiveCollection(comfort) {
  return comfort > 4;
}

// eslint-disable-next-line clubmed/no-fake-component
function getAccommodation({ accommodation, categoryLabel, resortComfort, streetView, i18n }) {
  const {
    areas,
    categoryId,
    comfortId,
    comfortType,
    description,
    floorPlans,
    id,
    label,
    media: { photos },
    productId,
  } = accommodation;
  const { strengths, strengthsCompact, strengthsAlternate } = mapAccommodationStrengths(
    i18n,
    accommodation,
  );
  const isDisplayable = [comfortType, label].every((item) => !!item);
  const isExclusiveCollection =
    isProductExclusiveCollection(resortComfort) || accommodation.exclusiveCollection;
  const images = photos.map(({ url }) => ({
    src: url,
  }));
  const streetViews =
    !isEmpty(streetView) && !!streetView.streetView ? [streetView.streetView] : undefined;
  const details = {
    areas,
    categoryId,
    comfort: resortComfort,
    comfortType,
    comfortId,
    description,
    floorPlans,
    immersive: {
      url: get(photos, '[0].url'),
    },
    isExclusiveCollection,
    label,
    categoryLabel,
    overview: strengths,
    overviewCompact: strengthsCompact,
    overviewAlternate: strengthsAlternate,
    strengthsLabel: i18n.strengthsLabel,
    amenitiesLabel: i18n.amenitiesLabel,
    services: mapAccommodationServices(i18n, accommodation, isExclusiveCollection),
    slideshow: {
      groupeKey: 'roomCard',
      media: images,
    },
    multimedia: {
      photos,
      streetViews,
    },
    productId,
  };
  return (
    isDisplayable && (
      <ProductRoomCard
        {...asWidgetProps<ComponentProps<typeof ProductRoomCard>>({
          fetchDetail: () => Promise.resolve(details),
          isExclusiveCollection,
          key: id,
          labeledIconsList: strengths,
          media: images,
          seeMoreLabel: i18n.seeMore,
          title: label,
        })}
      />
    )
  );
}

function renderDetail() {
  return ({
    areas,
    comfort,
    comfortType,
    description,
    floorPlans,
    isExclusiveCollection,
    immersive,
    label,
    categoryLabel,
    overview,
    overviewCompact,
    overviewAlternate,
    strengthsLabel,
    amenitiesLabel,
    services,
    slideshow,
    multimedia,
    productId,
  }) => {
    const customProps = {
      areas,
      description,
      title: label,
      subtitle: categoryLabel,
      diamond: {
        label: comfortType,
      },
      floorPlans,
      immersive,
      isExclusiveCollection: isProductExclusiveCollection(comfort) || isExclusiveCollection,
      overview,
      overviewCompact,
      overviewAlternate,
      strengthsLabel,
      amenitiesLabel,
      services,
      slideshow,
      multimedia,
      productId,
    };
    return <ProductRoomDetail {...customProps} />;
  };
}

export const getAllAvailableItems = (
  accommodationCategories: Array<AccommodationsCategory>,
  itemType: 'equipments' | 'services' | 'strengths' | 'bathroomEquipments',
) => {
  return sortByRanking(
    accommodationCategories.reduce((items, accommodationCategory) => {
      return unionBy(items, accommodationCategory[itemType], 'id');
    }, []),
  );
};

const isHighOrVeryHighRanking = ({ ranking }: { ranking?: string }) =>
  [RANKINGS.VERY_HIGH, RANKINGS.HIGH].includes(ranking);

export const withExclusiveCollectionTag = ({
  categoryId = '',
  isExclusiveCollection,
}: {
  categoryId?: string;
  isExclusiveCollection: boolean;
}) => {
  return ['typeoflodging_comfort_type_deluxe', 'typeoflodging_comfort_type_suite'].includes(
    categoryId,
  ) && isExclusiveCollection
    ? `${categoryId}_EC`
    : categoryId;
};
export function groupAccommodationsByCategory(props: BELineProps): Array<AccommodationsCategory> {
  const {
    accommodationCategory: { rankedCategories = [] } = {},
    bathroomLabel,
    capacityOfPeopleLabel,
    strengthsLabel,
    amenitiesLabel,
    equipmentLabel,
    numberOfPeopleLabel,
    resort: {
      accommodations = [],
      areas: resortAreas = [],
      comfort: resortComfort = 3,
      type,
      id: productId,
    },
    roomConfiguration: { singleRoom, connectingRooms, separatedRooms } = {},
    seeMore,
    servicesLabel,
    streetViews,
  } = props;
  const i18n = {
    bathroomLabel,
    capacityOfPeopleLabel,
    strengthsLabel,
    amenitiesLabel,
    connectingRooms,
    equipmentLabel,
    numberOfPeopleLabel,
    seeMore,
    separatedRooms,
    servicesLabel,
    singleRoom,
    surfaceLabel: t('accommodations.surfaceLabel'),
  };
  return asAccommodationsCategories(
    Object.values(
      accommodations.reduce(
        (categories: Record<string, AccommodationsCategory>, accommodation: Accommodation) => {
          // Ranking index to sort categories and accommodations within a category
          const rankedCategory = rankedCategories.find((c) => c.id === accommodation.categoryId);
          const categoryRanking = rankedCategory ? rankedCategory.ranking : DEFAULT_RANKING;
          const categoryIndex = getRankingIndex(categoryRanking);
          const streetView =
            streetViews && streetViews?.length > 0
              ? streetViews.find((sv) => sv.accommodationCategoryId === accommodation.categoryId)
              : [];
          // Accommodation tabs are now based on accommodation comfort, not category...
          let categoryId = accommodation.comfortId;
          let categoryLabel = accommodation.comfortType;

          // ...except for VILLA products
          if (type === PRODUCT_TYPES.VILLA) {
            // eslint-disable-next-line prefer-destructuring
            categoryId = accommodation.categoryId;
            categoryLabel = accommodation.category;
          }

          if (!categoryId) {
            return categories;
          }

          // Ranking index to sort accommodations within a category
          const accommodationIndex = getRankingIndex(accommodation.ranking);
          const accommodationProps = {
            accommodation: {
              ...accommodation,
              areas: (accommodation.areas || []).map((area) => ({
                id: area.id,
                label:
                  resortAreas.find(({ id: resortAreaId }) => resortAreaId === area.id)?.label || '',
              })),
              productId,
            },
            accommodationIndex,
            categoryLabel,
            categoryIndex,
            colorId: accommodation.categoryId,
            resortComfort,
            streetView,
            i18n: i18n as unknown as CMSProps,
          };
          const typedAccommodationProps =
            castAs<AccommodationsCategory['categoryAccommodations'][number]>(accommodationProps);
          categoryId = withExclusiveCollectionTag({
            categoryId,
            isExclusiveCollection: accommodation.exclusiveCollection,
          });

          if (categories[categoryId]) {
            // Add accommodation to category if it exists
            categories[categoryId].categoryAccommodations.push(typedAccommodationProps);

            if (
              !categories[categoryId].minCapacity ||
              (accommodation.minCapacity &&
                categories[categoryId].minCapacity > accommodation.minCapacity)
            ) {
              categories[categoryId].minCapacity = accommodation.minCapacity;
            }

            if (
              !categories[categoryId].maxCapacity ||
              (accommodation.maxCapacity &&
                categories[categoryId].maxCapacity < accommodation.maxCapacity)
            ) {
              categories[categoryId].maxCapacity = accommodation.maxCapacity;
            }

            if (
              accommodation.area?.min &&
              accommodation.area.min < categories[categoryId].area?.min
            ) {
              categories[categoryId].area.min = accommodation.area.min;
            }

            if (
              accommodation.area?.max &&
              accommodation.area?.max > categories[categoryId].area?.max
            ) {
              categories[categoryId].area.max = accommodation.area?.max;
            }

            categories[categoryId].exclusiveCollection =
              accommodation.exclusiveCollection && categories[categoryId].exclusiveCollection;
            categories[categoryId].equipments = castAs<AccommodationEquipment[]>(
              intersectionBy(
                categories[categoryId].equipments,
                castAs<AccommodationEquipment[]>(accommodation.equipments).filter(
                  isHighOrVeryHighRanking,
                ),
                'id',
              ),
            );
            categories[categoryId].services = castAs<AccommodationEquipment[]>(
              intersectionBy(
                categories[categoryId].services,
                castAs<AccommodationEquipment[]>(accommodation.services).filter(
                  isHighOrVeryHighRanking,
                ),
                'id',
              ),
            );
            categories[categoryId].strengths = castAs<AccommodationEquipment[]>(
              unionBy(
                categories[categoryId].strengths,
                castAs<AccommodationEquipment[]>([
                  ...(accommodation.view ? [accommodation.view] : []).map((v) => ({
                    ...v,
                    ranking: 1,
                  })),
                  ...(accommodation.strengths || []).map((strength) => ({
                    ...strength,
                    ranking: 2,
                  })),
                  ...(accommodation.outsideFacilities || []).map((outsideFacility) => ({
                    ...outsideFacility,
                    ranking: 3,
                  })),
                ]),
                'id',
              ).sort(
                (sA, sB) =>
                  castAs<{ ranking: number }>(sA).ranking - castAs<{ ranking: number }>(sB).ranking,
              ),
            );
            categories[categoryId].bathroomEquipments = intersectionBy(
              categories[categoryId].bathroomEquipments,
              accommodation.bathroomEquipments,
              'id',
            );
          } else {
            // Create category if it doesn't exist
            categories[categoryId] = castAs<AccommodationsCategory>({
              area: accommodation.area,
              bathroomEquipments: accommodation.bathroomEquipments,
              categoryAccommodations: [typedAccommodationProps],
              categoryId,
              categoryIndex,
              categoryLabel,
              equipments: castAs<AccommodationEquipment[]>(
                castAs<AccommodationEquipment[]>(accommodation.equipments).filter(
                  isHighOrVeryHighRanking,
                ),
              ),
              exclusiveCollection: accommodation.exclusiveCollection,
              media: accommodation.media,
              maxCapacity: accommodation.maxCapacity,
              minCapacity: accommodation.minCapacity,
              productId,
              services: castAs<AccommodationEquipment[]>(
                castAs<AccommodationEquipment[]>(accommodation.services).filter(
                  isHighOrVeryHighRanking,
                ),
              ),
              strengths: castAs<AccommodationEquipment[]>([
                ...(accommodation.view ? [accommodation.view] : []).map((v) => ({
                  ...v,
                  ranking: 1,
                })),
                ...(accommodation.strengths || []).map((strength) => ({ ...strength, ranking: 2 })),
                ...(accommodation.outsideFacilities || []).map((outsideFacility) => ({
                  ...outsideFacility,
                  ranking: 3,
                })),
              ]),
            });
          }

          return categories;
        },
        {},
      ),
    ).sort((a, b) => b.categoryIndex - a.categoryIndex),
  );
}
export function getTabsItemsAndPropsFromAccommodations(
  props: BELineProps,
  accommodationCategories: Array<AccommodationsCategory>,
) {
  const {
    isLineExclusiveCollection,
    resort: { isExclusiveCollection: resortIsExclusiveCollection },
  } = props;
  let hiddenItemsCount = 0;
  const items = // I have no idea what's going on there and to be honest I don't care enough
    // sorry to the person who'll be reading that in the future but I give up
    // -expect-error TODO(flow-migration)
    accommodationCategories
      .map((accommodationCategory, index) => {
        // -expect-error TODO(flow-migration)
        const { categoryAccommodations, categoryLabel } = accommodationCategory;
        // We need to sort with the upper index first. Since Array.sort is broken client side (why??),
        // we use the lodash function which only sorts from the lower to the upper. We knows that the
        // accomodations are already sorted by accomodationIdex so we have to sort by categoryIndex.
        // The tricky part is here: we cannot reverse the output of lodash.sortBy, so we sort with a
        // negative categoryIndex.
        const categoryAccommodationsProps = sortBy(
          [...categoryAccommodations],
          ({ categoryIndex }) => -1 * categoryIndex,
        );
        const content = categoryAccommodationsProps
          .map(
            getAccommodation as (
              props: (typeof categoryAccommodationsProps)[number],
            ) => ReturnType<typeof getAccommodation>,
          )
          .filter(Boolean);

        if (content.length === 0) {
          return null;
        }

        // -expect-error TODO(flow-migration)
        const isLarge = content.every((item) => item.props.isExclusiveCollection);
        const shouldDisplayAsExclusiveCollection =
          (isLarge && !resortIsExclusiveCollection) || resortIsExclusiveCollection;

        if ((isLineExclusiveCollection && isLarge) || !isLineExclusiveCollection) {
          return {
            header: {
              title: `${categoryLabel} ${isLarge && !resortIsExclusiveCollection ? t('common.exclusiveCollectionLabel') : ''}`,
              trackingData: {
                categoryGA: 'resort',
                actionGA: 'blockInfos',
                labelGA: `${isLineExclusiveCollection ? 'eCollection' : 'accommodations'}_${categoryLabel}`,
              },
              headerClassNames: shouldDisplayAsExclusiveCollection
                ? {
                    active: 'font-sans text-ultramarine',
                    inactive: 'font-sans',
                  }
                : {},
            },
            content: [
              <ProductInformationSliderContainer
                key={`ProductAccommodationsSlider_${categoryLabel}`}
                {...asWidgetProps<
                  ComponentProps<typeof ProductInformationSliderContainer> & {
                    theme?: string;
                    queryLayer: string;
                    renderDetail: (data: Record<string, unknown>) => ReactNode;
                    sliderIndex: number;
                  }
                >({
                  backNavigationLabel: t('common.backNavigation'),
                  theme: 'short',
                  errorMessage: 'error',
                  isLarge,
                  queryLayer: isLineExclusiveCollection
                    ? 'accommodationLayerEC'
                    : 'accommodationLayer',
                  renderDetail:
                    castAs<(data: Record<string, unknown>) => ReactNode>(renderDetail()),
                  scrollBackwardLabel: t('common.scrollBackward'),
                  scrollForwardLabel: t('common.scrollForward'),
                  sliderIndex: index - hiddenItemsCount,
                })}
              >
                {content}
              </ProductInformationSliderContainer>,
            ],
          };
        }

        hiddenItemsCount++;
        return null;
      })
      .filter((item) => item !== null);

  const slot = (activeIndexes: Array<number>) => (
    <ProductRoomsComparatorLauncher
      accommodationCategories={accommodationCategories}
      activeIndex={activeIndexes[0]}
      exclusiveCollectionLabel={t('common.exclusiveCollectionLabel')}
      resortIsExclusiveCollection={resortIsExclusiveCollection}
    />
  );

  return {
    items,
    slot,
  };
}
